import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { RequireModule } from '@/lib/entitlements';
import { MODULE_LIST } from '@/lib/modules';
import { HomeRedirect } from './home-redirect';
import { MODULE_PAGES, tienePagina } from './module-pages';
import { NotFoundScreen } from './not-found-page';
import { LOGIN_PATH, TENANT_PICKER_PATH } from './paths';
import { RootLayout } from './root-layout';
import { RouteError } from './route-error';

/**
 * Login arrives in its own chunk: it is the only screen with a validated form,
 * and an already-signed-in user — the common case — should not download a
 * form library plus its resolver to be redirected past it.
 */
const LoginPage = lazy(() => import('./login-page'));

/**
 * El selector de organización, también aparte.
 *
 * Ruta propia y no un paso dentro del login: es una pantalla con la sesión ya
 * abierta, así que recargarla no puede pedir la contraseña de nuevo. Y en
 * diferido por lo mismo que aquélla — el cliente de una sola organización, que
 * es el caso común, nunca la ve.
 */
const TenantPickerPage = lazy(() => import('./tenant-picker-page'));

/**
 * Every module owns everything under its path (`/leads/*`), arrives in its own
 * chunk, and is gated by `RequireModule` — so a disabled module never even
 * downloads, and its URL still answers with the "sin acceso" screen instead of
 * a redirect that would lose the link.
 *
 * LA EXCEPCIÓN ES EL MÓDULO RETIRADO, y es la única ruta de este archivo que
 * redirige. Un módulo apagado guarda su URL a propósito —se puede pegar en un
 * mensaje a quien administra el plan, y encendiéndolo vuelve—; uno retirado no
 * vuelve encendiendo nada, así que la pantalla de «sin acceso» sería un callejón
 * con un botón que no arregla nada. Se recorre `MODULE_LIST` entero y no
 * `OFFERED_MODULES` justamente para eso: sacarlo de la lista dejaría su
 * dirección cayendo en el 404, y un enlace viejo en un correo tiene que llegar
 * a alguna parte. Va a la raíz: `home-redirect` resuelve al primer módulo
 * que esta organización SÍ tiene, así que el destino nunca es una pantalla
 * de «sin acceso» — ni siquiera si alguien apagó el Resumen.
 *
 * Y EL MÓDULO SIN CONSTRUIR NO TIENE RUTA. No es un módulo apagado —ninguna
 * organización lo puede encender— ni uno retirado —nunca hubo un enlace que
 * honrar—: su «sin acceso» invitaría a encender en Módulos algo que no existe.
 * Su dirección cae en el 404, que es la verdad. Se reconoce por no tener página
 * (`tienePagina`), y que eso sea lo mismo que `unavailable` lo exige
 * `lib/niveles.test.ts`.
 */
const moduleRoutes: RouteObject[] = MODULE_LIST.flatMap((module) => {
  if (!tienePagina(module.id)) return [];
  const Page = MODULE_PAGES[module.id];
  return {
    path: `${module.path}/*`,
    errorElement: <RouteError />,
    element:
      module.retirado === true ? (
        <Navigate to="/" replace />
      ) : (
        <RequireModule id={module.id}>
          <Page />
        </RequireModule>
      ),
  };
});

export const router = createBrowserRouter([
  {
    path: LOGIN_PATH,
    element: (
      <Suspense fallback={<div className="h-full w-full bg-canvas" />}>
        <LoginPage />
      </Suspense>
    ),
    errorElement: <RouteError framed />,
  },
  {
    // Fuera de `RootLayout` a propósito: el cascarón exige organización activa y
    // acá justamente no hay ninguna todavía. Dibujarla adentro sería pedirle al
    // menú lateral que se arme con los módulos de una organización sin elegir.
    path: TENANT_PICKER_PATH,
    element: (
      <Suspense fallback={<div className="h-full w-full bg-canvas" />}>
        <TenantPickerPage />
      </Suspense>
    ),
    errorElement: <RouteError framed />,
  },
  {
    element: <RootLayout />,
    errorElement: <RouteError framed />,
    children: [
      { index: true, element: <HomeRedirect />, errorElement: <RouteError /> },
      ...moduleRoutes,
      { path: '*', element: <NotFoundScreen />, errorElement: <RouteError /> },
    ],
  },
]);
